import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';
import { EcapQueryHandlerResponse, Filter, GqlWatchResponse } from '../../shared/models/common.model';
import { GqlQueryBuilderService } from '../../shared/services/gql-query-builder.service';
import { GqlQueryCommandService } from '../../shared/services/gql-query-command.service';
import { PaginationService } from '../../shared/services/pagination.service';
import { EmailConversation } from '../model/email-conversation.model';
import { DepartmentInfo, InsuranceCompanySeed } from '../../shared/models/domain/InsuranceCompanySeed';
import { InsuranceCase } from '../../app-create-case/models/insurance-case.model';
import { EmailConversationWithCreatorProfileImageIds } from '../model/email-converstaions-with-creator-profile-image-ids.model';
import { CommonService } from '../../shared/services/common.service';
import { MailSearchFilter } from '../../app-cases/models/mail-search-filter';
import { DeleteEmailConversationCommand } from '../../shared/models/commandModels/DeleteEmailConversationCommand';
import { IpexCommandResponse } from '../../shared/models/responseModels/IpexCommandResponse';
import { UrlResolverService } from '../../shared/services/url/url-resolver.service';

@Injectable({
    providedIn: 'root'
})
export class AppMailboxService {
    private readonly urlResolver = inject(UrlResolverService);
    private headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'APIKey': '24b6f0e3-7b74-43d2-904a-372504f8dcf4'
    }); // Public key, not sensitive
    header: any = new HttpHeaders({
        'Content-Type': 'application/json'
    });
    private commonOptions: any = { headers: this.headers, withCredentials: true, observe: 'response' };
    currentDate = new Date();
    insuranceProviderList = new BehaviorSubject<InsuranceCompanySeed[]>([]);
    selectedInsuranceProvider = new BehaviorSubject<InsuranceCompanySeed>(new InsuranceCompanySeed());
    private caseCreatorImageIdsSubject = new BehaviorSubject<EmailConversationWithCreatorProfileImageIds[]>(
        new Array<EmailConversationWithCreatorProfileImageIds>()
    );
    public caseCreatorImageIds$: Observable<EmailConversationWithCreatorProfileImageIds[]> =
        this.caseCreatorImageIdsSubject.asObservable();
    isFromDeletedRoute = new BehaviorSubject<boolean>(false);
    selectedMail = new BehaviorSubject<EmailConversation>(null);
    filteredDepartment = new BehaviorSubject<DepartmentInfo[]>([]);
    $onApplyFilter = new BehaviorSubject<any>(null);
    noMailFoundForProvider = new BehaviorSubject<boolean>(false);
    departmentInfoList = new BehaviorSubject<DepartmentInfo[]>([]);
    selectedUnreadFilterOption = new BehaviorSubject<any>(null);
    toggleConnectMailToCase = new BehaviorSubject<EmailConversation>(null);
    toggleSearchMailOfMailBox = new BehaviorSubject<boolean>(false);
    isFetchConnectCase = new BehaviorSubject<boolean>(false);
    insuranceCompanyMapping = new BehaviorSubject<any>({});
    processCaseUrl = `${this.urlResolver.getPcxQueryUrl()}GetInsuranceCase`;
    pcxCommandUrl: string = this.urlResolver.getPcxCommandUrl();
    pcxQueryUrl: string = this.urlResolver.getPcxQueryUrl();
    constructor(
        private gqlQueryBuilderService: GqlQueryBuilderService,
        private gqlQueryCommandService: GqlQueryCommandService,
        private paginationService: PaginationService,
        private http: HttpClient
    ) {}

    getInsuranceCompanyList(ItemId?): Observable<GqlWatchResponse<InsuranceCompanySeed>> {
        const entityName = SCConfig.Entity.InsuranceCompanySeed;
        const fields = SCConfig.UserReadableDatas.InsuranceCompanySeed;
        const filters: Filter[] = [];
        if (ItemId) {
            filters.push({
                operator: 'raw',
                value: `'_id': '${ItemId}'`
            });
        }
        return this.gqlQueryCommandService.gqlWatchQuery<InsuranceCompanySeed>(
            entityName,
            fields,
            filters,
            'LastUpdateDate:1'
        );
    }

    getEmailList(
        pagination: PageEvent,
        provider: InsuranceCompanySeed,
        isFilterSearch: boolean,
        unreadFilterOption: any
    ): Observable<GqlWatchResponse<EmailConversation>> {
        const entityName = SCConfig.Entity.EmailConversation;
        const fields = SCConfig.UserReadableDatas.EmailConversation;
        const filters: Filter[] = [];
        if (!isFilterSearch) {
            if (provider && provider.ItemId) {
                filters.push(new Filter('InsuranceCompanyId', '=', provider.ItemId));
            }
        } else {
            filters.push(new Filter('DepartmentId', '=', provider.DepartmentInfoList[0].DepartmentId));
        }
        if (unreadFilterOption == true) {
            filters.push(new Filter('IsRead', '=', true));
        } else if (unreadFilterOption == false) {
            filters.push(new Filter('IsRead', '=', false));
        }

        return this.gqlQueryCommandService.gqlWatchQuery<EmailConversation>(
            entityName,
            fields,
            filters,
            'ReceivedDateTime:-1',
            pagination ? pagination.pageIndex : 0,
            10
        );
    }

    getEmailListWithCorrectPagination(
        pagination: PageEvent,
        provider: InsuranceCompanySeed,
        isFilterSearch: boolean,
        value,
        property = '_id'
    ): Observable<GqlWatchResponse<EmailConversation> & { PageNumber: number; Index: number }> {
        const filters: Filter[] = [];
        if (!isFilterSearch) {
            filters.push(new Filter('InsuranceCompanyId', '=', provider.ItemId));
        } else {
            filters.push(new Filter('DepartmentId', '=', provider.DepartmentInfoList[0].DepartmentId));
        }
        return this.paginationService.correctedPagination<EmailConversation>(
            property,
            value,
            SCConfig.Entity.EmailConversation,
            SCConfig.UserReadableDatas.EmailConversation,
            filters,
            pagination.pageSize,
            'ReceivedDateTime:-1'
        );
    }

    getEmailConversionByIdOrEmailId(itemId?: string, emailId?: string): Observable<EmailConversation> {
        const entityName = SCConfig.Entity.EmailConversation;
        const fields = SCConfig.UserReadableDatas.EmailConversation;
        const filters: Filter[] = [];
        if (itemId) {
            filters.push(new Filter('_id', '=', itemId));
        } else if (emailId) {
            filters.push(new Filter('EmailId', '=', emailId));
        } else {
            return of(null);
        }

        return this.gqlQueryCommandService
            .gqlWatchQuery<EmailConversation>(entityName, fields, filters, 'ReceivedDateTime:-1', 0, 1)
            .pipe(map(response => (response.Data.length ? response.Data[0] : null)));
    }

    getTimeDifference(date) {
        const difference = this.currentDate.getTime() - new Date(date).getTime(); // This will give difference in milliseconds
        const resultInMinutes = Math.floor(difference / 60000);
        let resultInHour = Math.floor(resultInMinutes / 60).toString();
        let remainingMinutes = (resultInMinutes % 60).toString();
        resultInHour = resultInHour.length === 1 ? '0' + resultInHour : resultInHour;
        remainingMinutes = remainingMinutes.length === 1 ? '0' + remainingMinutes : remainingMinutes;
        return resultInHour + ':' + remainingMinutes + ' HRS';
    }

    getUnreadCount(): Observable<any> {
        const unreadCountgDocProcessingIncomplete = `UnreadCountgDocProcessingIncomplete: ${this.gqlQueryBuilderService.buildDistinctQuery(
            SCConfig.Entity.EmailConversation,
            `{IsRead : false , DocumentProcessingStatus : '${SCConfig.DocumentProcessingStatus.PROCESSING}'}`,
            `{'_id': '$InsuranceCompanyId', 'Count': {'$sum': 1}}`,

            0,

            100
        )}`;

        const unreadCountgDocProcessingcomplete = `UnreadCountgDocProcessingcomplete: ${this.gqlQueryBuilderService.buildDistinctQuery(
            SCConfig.Entity.EmailConversation,
            `{IsRead : false , DocumentProcessingStatus : { $ne: '${SCConfig.DocumentProcessingStatus.PROCESSING}' }}`,
            `{'_id': '$InsuranceCompanyId', 'Count': {'$sum': 1}}`,

            0,

            100
        )}`;
        return this.gqlQueryCommandService
            .gqlAggregateWatchQuery([unreadCountgDocProcessingIncomplete, unreadCountgDocProcessingcomplete])
            .pipe(
                map(response => {
                    const unreadCountOnProcessing = new Map<string, number>();
                    const unreadCountAfterProcessing = new Map<string, number>();
                    if (
                        response.data.UnreadCountgDocProcessingIncomplete &&
                        response.data.UnreadCountgDocProcessingIncomplete.Data.length
                    ) {
                        response.data.UnreadCountgDocProcessingIncomplete.Data.forEach(providerMailCount => {
                            const unReadCount = providerMailCount.Count;
                            unreadCountOnProcessing.set(providerMailCount._id, unReadCount);
                        });
                    }
                    if (
                        response.data.UnreadCountgDocProcessingcomplete &&
                        response.data.UnreadCountgDocProcessingcomplete.Data.length
                    ) {
                        response.data.UnreadCountgDocProcessingcomplete.Data.forEach(providerMailCount => {
                            const unReadCount = providerMailCount.Count;
                            unreadCountAfterProcessing.set(providerMailCount._id, unReadCount);
                        });
                    }

                    return {
                        unreadCountgDocProcessingIncomplete: unreadCountOnProcessing,
                        unreadCountgDocProcessingcomplete: unreadCountAfterProcessing
                    };
                })
            );
    }

    public getInsuranceSeedData(ItemId?): Observable<InsuranceCompanySeed[]> {
        const uri = `${this.urlResolver.getPcxQueryUrl()}GetInsuranceCompanySeed`;
        return this.http.post(uri, { EntityName: 'SeedData' }, SCConfig.HttpCommonOptions).pipe(
            map((response: any) => {
                if (ItemId) {
                    let data = response.filter(provider => provider.ItemId === ItemId);
                    return data;
                } else {
                    return response;
                }
            })
        );
    }
    getCaseListByBusinessServiceForConnectMail(
        searchKey: string,
        departmentId: string,
        emailItemId: string,
        pageNo: number
    ) {
        let filters = [
            new Filter('IsDraft', '=', false),
            new Filter(null, 'raw', `'EmailConversationInfos.ItemId': {$nin: ["${emailItemId}"]}`)
        ];
        if (departmentId !== null) {
            filters.push(new Filter(`'CaseReferenceInfo.DepartmentId'`, '=', departmentId));
        }
        if (searchKey) {
            searchKey = CommonService.sanitizeSearchString(searchKey);
            filters.push(
                new Filter(
                    null,
                    'raw',
                    `$or: [{InsuranceClaimNumber: ${new RegExp(searchKey, 'i')}}, {CaseReportNumber: ${new RegExp(
                        searchKey,
                        'i'
                    )}}, {'PrimaryEmailConversation.Subject': ${new RegExp(searchKey, 'i')}}]`
                )
            );
        }
        const processCaseUrl = `${this.urlResolver.getPcxQueryUrl()}GetInsuranceCase`;

        const sortBy = '{CreateDate: -1}';

        const payload = {
            SortBy: sortBy,
            FilterString: `{${this.gqlQueryBuilderService.prepareFilters(filters)}}`,
            PageNumber: pageNo,
            PageSize: 10
        };

        return this.http
            .post(processCaseUrl, payload, { headers: this.header, withCredentials: true, observe: 'body' })
            .pipe(
                map((response: EcapQueryHandlerResponse<InsuranceCase[]>) => {
                    return response;
                })
            );
    }

    connectCaseWithEmail(payload) {
        // const processCaseUrl = `${this.urlResolver.getPcxQueryUrl()}GetInsuranceCase`;
        const processCaseUrl = `${this.urlResolver.getPcxCommandUrl()}ConnectCaseWithEmail`;

        return this.http
            .post(processCaseUrl, payload, { headers: this.header, withCredentials: true, observe: 'response' })
            .pipe(
                map((response: any) => {
                    return response.body;
                })
            );
    }

    getInsuranceCasesByEmailId(emailId: string) {
        const filters = [
            new Filter('IsDraft', '=', false),
            new Filter(null, 'raw', `'EmailConversationInfos.ItemId': {$in: ["${emailId}"]}`)
        ];
        const sortBy = '{CreateDate: -1}';
        const payload = {
            SortBy: sortBy,
            FilterString: `{${this.gqlQueryBuilderService.prepareFilters(filters)}}`,
            PageNumber: 0,
            PageSize: 10
        };
        return this.http
            .post(this.processCaseUrl, payload, { headers: this.header, withCredentials: true, observe: 'body' })
            .pipe(
                map((response: EcapQueryHandlerResponse<InsuranceCase>) => {
                    return response;
                })
            );
    }
    getEmailListByBusinessService(
        pagination: PageEvent,
        insuranceCompany: InsuranceCompanySeed,
        isFilterSearch: boolean,
        unreadFilterOption: any,
        sortBy: string = '{ CreateDate: 1 }',
        itemId: string = '',
        DepartmentInfoList: DepartmentInfo[] = []
    ) {
        const filters = [];
        if (insuranceCompany && insuranceCompany.ItemId) {
            filters.push(new Filter('InsuranceCompanyId', '=', insuranceCompany.ItemId));
        }
        if (!itemId) {
            const departmentIds = (DepartmentInfoList || []).map(item => item.DepartmentId);
            if (departmentIds.length > 0) {
                filters.push(new Filter(`DepartmentId`, 'in', departmentIds));
            }
        }

        if (unreadFilterOption == true) {
            filters.push(new Filter('IsRead', '=', true));
        } else if (unreadFilterOption == false) {
            filters.push(new Filter('IsRead', '=', false));
        }
        if (itemId) {
            filters.push(new Filter('_id', '=', itemId));
        }
        const payload = {
            SortBy: sortBy,
            FilterString: `{${this.gqlQueryBuilderService.prepareFilters(filters)}}`,
            PageNumber: pagination ? pagination.pageIndex : 0,
            PageSize: 10
        };
        return this.http
            .post(this.pcxQueryUrl + 'GetEmailConversation', payload, {
                headers: this.header,
                withCredentials: true,
                observe: 'body'
            })
            .pipe(
                map((response: EcapQueryHandlerResponse<EmailConversation[]>) => {
                    return response;
                })
            );
    }

    getEmailListByBusinessServiceById(itemId: string) {
        const filters = [];
        if (itemId && itemId != '') {
            filters.push(new Filter('_id', '=', itemId));
        }
        const sortBy = '{CreateDate: -1}';
        const payload = {
            SortBy: sortBy,
            FilterString: `{${this.gqlQueryBuilderService.prepareFilters(filters)}}`,
            PageNumber: 0,
            PageSize: 10
        };
        return this.http
            .post(this.pcxQueryUrl + 'GetEmailConversation', payload, {
                headers: this.header,
                withCredentials: true,
                observe: 'body'
            })
            .pipe(
                map((response: EcapQueryHandlerResponse<EmailConversation>) => {
                    return response;
                })
            );
    }

    getEmailListBySearchFilter(pageNo: number, mailSearchFilter: MailSearchFilter = null) {
        const filters = [];
        const { SearchMailValue, CompanyIds } = mailSearchFilter;
        if (SearchMailValue) {
            let searchKeyForQuery = `/${CommonService.sanitizeSearchString(SearchMailValue)}/i`;
            filters.push(
                new Filter(
                    null,
                    'raw',
                    `$or: [{Subject: ${searchKeyForQuery}}, {'From.Name': ${searchKeyForQuery}}, {'From.Address': ${searchKeyForQuery}}]`
                )
            );
        }
        if (CompanyIds.length > 0) {
            filters.push(new Filter('InsuranceCompanyId', 'in', mailSearchFilter?.CompanyIds));
        }
        const sortBy = '{CreateDate: -1}';
        const payload = {
            SortBy: sortBy,
            FilterString: `{${this.gqlQueryBuilderService.prepareFilters(filters)}}`,
            PageNumber: pageNo,
            PageSize: 15
        };
        return this.http
            .post(this.pcxQueryUrl + 'GetEmailConversation', payload, {
                headers: this.header,
                withCredentials: true,
                observe: 'body'
            })
            .pipe(
                map((response: EcapQueryHandlerResponse<EmailConversation[]>) => {
                    return response;
                })
            );
    }

    public getCaseCreatorImageIdsByEmailConversationIds(
        emailConversationIds: string[]
    ): Observable<EmailConversationWithCreatorProfileImageIds[]> {
        const payload = {
            EmailConversationIds: emailConversationIds
        };
        return this.http
            .post(this.pcxQueryUrl + 'GetCaseCreatorProfileImagesByEmailConversationIds', payload, {
                headers: this.header,
                withCredentials: true,
                observe: 'body'
            })
            .pipe(
                map((queryResponse: EcapQueryHandlerResponse<any>) => {
                    if (queryResponse.StatusCode === 1) {
                        if (
                            queryResponse.Data !== null &&
                            queryResponse.Data.EmailConversationsWithCreatorProfileImageIds !== null &&
                            queryResponse.Data.EmailConversationsWithCreatorProfileImageIds.length > 0
                        ) {
                            return Array<EmailConversationWithCreatorProfileImageIds>(
                                ...queryResponse.Data.EmailConversationsWithCreatorProfileImageIds
                            );
                        }
                    }
                    return [];
                })
            );
    }

    public pushCaseCreatorImageIds(
        emailConversationWithCreatorProfileImageIds: EmailConversationWithCreatorProfileImageIds[]
    ) {
        this.caseCreatorImageIdsSubject.next(emailConversationWithCreatorProfileImageIds);
    }

    public DeleteEmailConversation(payload: DeleteEmailConversationCommand) {
        return this.http.post<IpexCommandResponse<string>>(
            this.pcxCommandUrl + 'DeleteEmailConversation',
            payload,
            SCConfig.HttpCommonPostOptions
        );
    }
}
